/**
 * User-scoped saved spatial-analysis reports.
 *
 * Reports are owned by the user (not a map), so a user sees their saved reports
 * across every map. The durable home is the simulator (`data_generator`) backend
 * (HTTM, where user auth lives). Because that endpoint may not be deployed in an
 * environment yet, each call is simulator-first with a rollout-safe fallback to a
 * Atlas backend store (mirrors `editMapWithFallback`).
 *
 * If neither backend is available, the calls throw `ReportsUnavailableError` so
 * the UI can show an "under construction" placeholder instead of an error.
 */

import axios from 'axios';
import { atlasBackendApi } from '../api/atlasBackendClient';

import { getAtlasApiBaseUrl, getSimulationApiBaseUrl } from '../getAtlasApiBaseUrl';
import {
  SpatialAnalysisLayer,
  SpatialAnalysisMetrics,
  SpatialAnalysisReportSection,
  SpatialAnalysisSource,
} from './types';

const SIMULATOR_REPORTS_BASE = `${getSimulationApiBaseUrl()}/data_generator/user/spatial-analysis-reports`;
const FALLBACK_REPORTS_BASE = `${getAtlasApiBaseUrl()}/api/spatial-analysis/reports`;

export interface SavedReportSummary {
  report_id: string;
  title: string;
  created_at: string | null;
  role?: string;
}

export interface SavedReport extends SavedReportSummary {
  source_summary?: SpatialAnalysisSource | Record<string, unknown>;
  metrics?: SpatialAnalysisMetrics;
  report_sections?: SpatialAnalysisReportSection[];
  layers?: SpatialAnalysisLayer[];
}

export interface SaveReportInput {
  title: string;
  source_summary?: SpatialAnalysisSource | Record<string, unknown>;
  metrics?: SpatialAnalysisMetrics;
  report_sections?: SpatialAnalysisReportSection[];
  layers?: SpatialAnalysisLayer[];
}

/** Thrown when neither the simulator nor the fallback report store is available. */
export class ReportsUnavailableError extends Error {
  constructor() {
    super('Saved reports are not available yet');
    this.name = 'ReportsUnavailableError';
  }
}

function getAuthToken(): string {
  const tokenString = localStorage.getItem('atlas_authToken');
  if (!tokenString) {
    throw new ReportsUnavailableError();
  }
  try {
    const parsed = JSON.parse(tokenString);
    if (parsed?.access_token) {
      return parsed.access_token;
    }
  } catch {
    // Not JSON — use the raw string.
  }
  return tokenString;
}

function authHeaders(): Record<string, string> {
  return { Authorization: `Bearer ${getAuthToken()}` };
}

/** An endpoint that isn't deployed (404/405/501) or unreachable (no response) counts as "unavailable". */
function isUnavailable(error: unknown): boolean {
  if (error instanceof ReportsUnavailableError) {
    return true;
  }
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    if (status === undefined) {
      return true; // network / CORS / connection refused
    }
    return status === 404 || status === 405 || status === 501;
  }
  return false;
}

/** Try the simulator endpoint first; fall back to the atlas store; else mark unavailable. */
async function withFallback<T>(simulator: () => Promise<T>, fallback: () => Promise<T>): Promise<T> {
  try {
    return await simulator();
  } catch (simError) {
    if (!isUnavailable(simError)) {
      throw simError;
    }
    try {
      return await fallback();
    } catch (fallbackError) {
      if (isUnavailable(fallbackError)) {
        throw new ReportsUnavailableError();
      }
      throw fallbackError;
    }
  }
}

export async function saveSpatialAnalysisReport(input: SaveReportInput): Promise<{ report_id: string }> {
  return withFallback(
    async () => (await axios.post(SIMULATOR_REPORTS_BASE, input, { headers: authHeaders(), timeout: 15000 })).data,
    async () => (await atlasBackendApi.post(FALLBACK_REPORTS_BASE, input, { headers: authHeaders(), timeout: 15000 })).data,
  );
}

export async function listSpatialAnalysisReports(): Promise<SavedReportSummary[]> {
  const data = await withFallback(
    async () => (await axios.get(SIMULATOR_REPORTS_BASE, { headers: authHeaders(), timeout: 15000 })).data,
    async () => (await atlasBackendApi.get(FALLBACK_REPORTS_BASE, { headers: authHeaders(), timeout: 15000 })).data,
  );
  return (data?.reports as SavedReportSummary[] | undefined) ?? [];
}

export async function getSpatialAnalysisReport(reportId: string): Promise<SavedReport> {
  return withFallback(
    async () => (await axios.get(`${SIMULATOR_REPORTS_BASE}/${reportId}`, { headers: authHeaders(), timeout: 15000 })).data,
    async () => (await atlasBackendApi.get(`${FALLBACK_REPORTS_BASE}/${reportId}`, { headers: authHeaders(), timeout: 15000 })).data,
  );
}

export async function deleteSpatialAnalysisReport(reportId: string): Promise<void> {
  await withFallback(
    async () => (await axios.delete(`${SIMULATOR_REPORTS_BASE}/${reportId}`, { headers: authHeaders(), timeout: 15000 })).data,
    async () => (await atlasBackendApi.delete(`${FALLBACK_REPORTS_BASE}/${reportId}`, { headers: authHeaders(), timeout: 15000 })).data,
  );
}

export async function renameSpatialAnalysisReport(reportId: string, title: string): Promise<void> {
  await withFallback(
    async () => (await axios.patch(`${SIMULATOR_REPORTS_BASE}/${reportId}`, { title }, { headers: authHeaders(), timeout: 15000 })).data,
    async () => (await atlasBackendApi.patch(`${FALLBACK_REPORTS_BASE}/${reportId}`, { title }, { headers: authHeaders(), timeout: 15000 })).data,
  );
}
